import React from "react";
import Button from "react-bootstrap/Button";
import ReactBadge from "react-bootstrap/Badge";
import { labelForCallGroupType, labelForOutcomeTag } from "./aiCallLogConstants";
import {
  getCallGroupActivityUrl,
  getPolyStudioConversationUrl,
} from "./buildAiCallLogLinks";
import { formatAiCallLogInstant, outboundStatusVariant } from "./aiCallLogColumnFormatters";

const WrappedBadge = (props) => (
  <div>
    <ReactBadge {...props}>{props.children}</ReactBadge>
  </div>
);

const PillBadge = ({ children, bg }) => (
  <ReactBadge pill bg={bg} className="fw-normal">
    {children}
  </ReactBadge>
);

/** Comma-separated switch info IDs (`AiCallLogRowDto.switchInfoIds`). */
export function AiCallLogSwitchesCell({ value }) {
  if (!value?.length) {
    return <span className="text-muted">—</span>;
  }
  const ids = Array.isArray(value) ? value : [];
  const text = ids
    .map((id) => (id == null || id === "" ? "" : String(id)))
    .filter((s) => s !== "")
    .join(", ");
  if (!text) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div
      style={{
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        lineHeight: 1.35,
        textAlign: "left",
      }}
    >
      {text}
    </div>
  );
}

function normalizeOutcomeTags(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return [...value].filter(Boolean).sort();
  if (typeof value === "object" && value !== null && Symbol.iterator in value) {
    try {
      return [...value].filter(Boolean).sort();
    } catch {
      return [];
    }
  }
  return [];
}

export function AiCallLogOutcomeTagsCell({ value }) {
  const tags = normalizeOutcomeTags(value);
  if (!tags.length) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      {tags.map((raw, i) => (
        <PillBadge key={`${raw}-${i}`} bg="dark">
          {labelForOutcomeTag(raw)}
        </PillBadge>
      ))}
    </div>
  );
}

export function AiCallLogStringListCell({ value, badgeBg = "secondary" }) {
  if (!value?.length) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      {value.map((item, i) => (
        <PillBadge key={`${item}-${i}`} bg={badgeBg}>
          {item}
        </PillBadge>
      ))}
    </div>
  );
}

export function AiCallLogCallGroupTypeCell({ value }) {
  return (
    <WrappedBadge pill variant="secondary">
      {labelForCallGroupType(value)}
    </WrappedBadge>
  );
}

export function AiCallLogOutboundStatusCell({ value }) {
  return (
    <WrappedBadge pill variant={outboundStatusVariant(value)}>
      {value}
    </WrappedBadge>
  );
}

export function AiCallLogListenCell({ value }) {
  const url = getPolyStudioConversationUrl(value);
  return url ? (
    <Button size="sm" variant="primary" href={url} target="_blank" rel="noopener noreferrer">
      Listen
    </Button>
  ) : (
    <span className="text-muted">—</span>
  );
}

export function AiCallLogViewCell({ value }) {
  const url = getCallGroupActivityUrl(value);
  return url ? (
    <Button size="sm" variant="primary" href={url} target="_blank" rel="noopener noreferrer">
      View
    </Button>
  ) : (
    <span className="text-muted">—</span>
  );
}

export function AiCallLogDateTimeCell({ value }) {
  return <span>{formatAiCallLogInstant(value)}</span>;
}
